import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { FileType } from "@/Common/Models/FileType";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ExportableFile, IExportableFile, IExportableFileLoaded } from "../ExportableFile";
import { GenerateFileOptions } from "../IGenerateFileOptions";
import { FileTypeTemporaryFile } from "./FileTypeTemporaryFile";

interface IFileType_WorkerClaimCnesst extends IExportableFile {
    workerClaimId: string;
    fromPatientDocId?: string;
    fromPatientDocType?: FileType;
    isCnesstFormFinalPrint?: boolean;
    patientContactId?: string;
    patientContactFacetId?: string;
    finalPdfId?: string;
}

class FileTypeWorkerClaimCnesst extends ExportableFile implements IFileType_WorkerClaimCnesst {
    public canGenerateFileXcom: boolean = true;

    public workerClaimId: string;
    public fromPatientDocId: string;
    public fromPatientDocType: FileType;

    public isCnesstFormFinalPrint: boolean = false;

    public patientContactId: string;
    public patientContactFacetId: string;

    public finalPdfId: string;

    constructor(fields?: IFileType_WorkerClaimCnesst) {
        super(FileType.WORKERCLAIM);
        Object.assign(this, fields);
    }

    public getAttachedObject_ObjectId(): string { return this.workerClaimId; }

    public getCleanCopy(): FileTypeWorkerClaimCnesst {
        const copy = new FileTypeWorkerClaimCnesst();
        Object.assign(copy, this);
        return copy;
    }

    public async getMeta(): Promise<number> {
        await this.generateFileIfNeeded({ includeOverlays: true, isExternal: false });
        return this.totalPages;
    }

    protected async generateFileIfNeededXcom(includeOverlays: boolean): Promise<IExportableFileLoaded> {
        return await this.generateFileIfNeeded({ includeOverlays: includeOverlays, isExternal: true });
    }

    protected async generateFileIfNeeded(generateFileOption: GenerateFileOptions): Promise<IExportableFileLoaded> {
        const { includeOverlays, isExternal } = generateFileOption;
        let requestedOverlays = (includeOverlays && this.overlayElements) || [];

        const response: ClientResponse<{ OverflowFields?: string[], TotalPages: number, ObjectId: string, SizeBytes: number }> =
            isExternal
                ? await Client.postAsyncXcom(
                    ServerOperations.Server.Myle_CnesstModule_GetWorkerClaimExportableFile,
                    {
                        OverlayElements: this.serializeOverlayElements(includeOverlays),
                        workerClaimFormId: this.workerClaimId,
                        isCnesstFormFinalPrint: this.isCnesstFormFinalPrint,
                        patientContactId: this.patientContactId,
                        patientContactFacetId: this.patientContactFacetId
                    },
                    this.clientTag,
                    this.claimId
                )
                : await Client.postAsync(
                    ServerOperations.Server.Myle_CnesstModule_GetWorkerClaimExportableFile,
                    {
                        OverlayElements: this.serializeOverlayElements(includeOverlays),
                        workerClaimFormId: this.workerClaimId,
                        isCnesstFormFinalPrint: this.isCnesstFormFinalPrint,
                        patientContactId: this.patientContactId,
                        patientContactFacetId: this.patientContactFacetId
                    }
                );

        if (!response.isSuccess)
            throw new Error(response.errors[0].Message);

        if (response.data.OverflowFields?.any())
            sessionStore.showPopup({
                title: cacheStore.GetString("WarningLabel"),
                content: [cacheStore.GetString("Cnesst_PdfWarning_Message")].concat(response.data.OverflowFields),
                contentStyle: "list-after-first"
            });

        this.totalPages = response.data.TotalPages;

        let tempFile = new FileTypeTemporaryFile({
            ObjectId: response.data.ObjectId,
            totalPages: response.data.TotalPages,
            sizeBytes: response.data.SizeBytes,
            date: this.date,
            description: this.description
        });

        tempFile.isExternal = isExternal;
        if (tempFile.isExternal) {
            tempFile.clientTag = this.clientTag;
            tempFile.claimId = this.claimId;
        }

        super.setCache(tempFile, requestedOverlays);

        return {
            OriginalExportableFile: this,
            PregeneratedTemporaryFile: tempFile,
            FetchUrl:
                isExternal
                    ? tempFile.FetchUrlXcom(this.clientTag, this.claimId)
                    : tempFile.FetchUrl
        } as IExportableFileLoaded;
    }

    public static fromWorkerClaimForm(
        id: string,
        description: string,
        formDate: Date,
        isCnesstFormFinalPrint: boolean = false,
        patientContactId: string = null,
        patientContactFacetId: string = null,
        finalPdfId: string = null
    ): FileTypeWorkerClaimCnesst {
        return new FileTypeWorkerClaimCnesst({
            workerClaimId: id,
            description: description,
            date: formDate.toString(),
            isCnesstFormFinalPrint: isCnesstFormFinalPrint,
            patientContactFacetId: patientContactFacetId,
            patientContactId: patientContactId,
            finalPdfId: finalPdfId
        });
    }
}

export { FileTypeWorkerClaimCnesst };
